import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Button, LoadingContent, Select } from '../../../components'
import PdcaLayout from '../base/pdcaLayout'
import PdcaPageHeader from '../components/pdcapageheader/pdcapageheader'
import PdcaEmptyState from '../components/pdcaemptystate/pdcaemptystate'
import PdcaPlanoCard from '../components/pdcaplanocard/pdcaplanocard'
import Detalheplano from '../detalheplano/detalheplano'
import RejeitarPlano from '../rejeitarplano/rejeitarplano'
import AprovarAcao from '../aprovaracao/aprovaracao'
import { setPeriodoPdca } from '../base/redux/pdcaBaseActions'
import { fetchDo, clearDo } from './redux/doActions'
import './do.scss'

const obterTipoPlano = (tipoPlano, nls) => {
  const mapa = {
    diario: nls.planTypeDaily,
    semanal: nls.planTypeWeekly,
    mensal: nls.planTypeMonthly
  }

  return mapa[tipoPlano] || tipoPlano
}

const obterEscopo = (item, nls) => {
  if (item.lojaNome) return `${nls.scopeStore}: ${item.lojaNome}`
  if (item.produtoNome) return `${nls.scopeProduct}: ${item.produtoNome}`
  return nls.scopeCompany
}

const obterStatus = (status, nls) => {
  const mapa = {
    em_execucao: nls.em_execucao,
    atrasado: nls.atrasado,
    atraso_critico: nls.atraso_critico,
    rejeitado: nls.rejeitado
  }

  return mapa[status] || status
}

const obterVariantStatus = status => {
  if (status === 'atrasado' || status === 'atraso_critico') return 'danger'
  if (status === 'em_execucao') return 'warning'
  return 'neutral'
}

const formatarDataInput = data => {
  const ano = data.getFullYear()
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}

const obterHojeInput = () => formatarDataInput(new Date())

const adicionarDiasInput = (dataTexto, dias) => {
  if (!dataTexto) return ''

  const [ano, mes, dia] = dataTexto.split('-').map(Number)
  const data = new Date(ano, mes - 1, dia)
  data.setDate(data.getDate() + dias)

  return formatarDataInput(data)
}

const diferencaDiasInput = (dataInicialTexto, dataFinalTexto) => {
  if (!dataInicialTexto || !dataFinalTexto) return 0

  const [anoInicial, mesInicial, diaInicial] = dataInicialTexto.split('-').map(Number)
  const [anoFinal, mesFinal, diaFinal] = dataFinalTexto.split('-').map(Number)

  const dataInicial = new Date(anoInicial, mesInicial - 1, diaInicial)
  const dataFinal = new Date(anoFinal, mesFinal - 1, diaFinal)

  const diffMs = dataFinal.getTime() - dataInicial.getTime()
  return Math.floor(diffMs / 86400000)
}

export default () => {
  const dispatch = useDispatch()
  const { idProjetos } = useParams()

  const { sistema, projeto } = useSelector(state => state.layoutState)
  const nls = require(`./nls/${sistema.idioma}.json`)

  const {
    projetoId,
    empresaPdcaExiste,
    empresaPdcaId,
    contextoCarregado,
    dataInicial: dataInicialGlobal,
    dataFinal: dataFinalGlobal
  } = useSelector(state => state.pdcaState.pdcaBaseState)

  const {
    itens = [],
    total = 0,
    loading = false,
    error = null
  } = useSelector(state => state.pdcaState.pdcaDoState || {})

  const statusOptions = useMemo(() => ([
    { label: nls.todos, value: 'todos' },
    { label: nls.em_execucao, value: 'em_execucao' },
    { label: nls.atrasado, value: 'atrasado' },
    { label: nls.atraso_critico, value: 'atraso_critico' },
    { label: nls.rejeitado, value: 'rejeitado' }
  ]), [
    nls.todos,
    nls.em_execucao,
    nls.atrasado,
    nls.atraso_critico,
    nls.rejeitado
  ])

  const [dataInicial, setDataInicial] = useState(dataInicialGlobal || '')
  const [dataFinal, setDataFinal] = useState(dataFinalGlobal || '')
  const [erroFiltro, setErroFiltro] = useState(null)
  const [statusSelecionado, setStatusSelecionado] = useState(statusOptions[0] || {})
  const [detalheOpen, setDetalheOpen] = useState(false)
  const [planoIdSelecionado, setPlanoIdSelecionado] = useState(null)
  const [rejeitarOpen, setRejeitarOpen] = useState(false)
  const [planoIdRejeitar, setPlanoIdRejeitar] = useState(null)
  const [nomePlanoRejeitar, setNomePlanoRejeitar] = useState('')
  const [aprovarOpen, setAprovarOpen] = useState(false)
  const [planoIdAprovar, setPlanoIdAprovar] = useState(null)
  const [nomePlanoAprovar, setNomePlanoAprovar] = useState('')

  const contextoValido =
    contextoCarregado &&
    String(projetoId) === String(idProjetos)

  const podeGerirProjeto = projeto?.permissaoPdcaGerirProjeto === true

  useEffect(() => {
    setDataInicial(dataInicialGlobal || '')
    setDataFinal(dataFinalGlobal || '')
  }, [dataInicialGlobal, dataFinalGlobal])

  useEffect(() => {
    setStatusSelecionado(statusOptions[0] || {})
  }, [statusOptions])

  useEffect(() => {
    return () => {
      dispatch(clearDo())
    }
  }, [dispatch])

  useEffect(() => {
    if (!idProjetos || !contextoValido || !empresaPdcaExiste) {
      return
    }

    if (!dataInicialGlobal || !dataFinalGlobal) {
      return
    }

    dispatch(fetchDo({
      empresaId: empresaPdcaId,
      projetoId: idProjetos,
      idiomaId: sistema.idiomaIdValue,
      dataInicial: dataInicialGlobal,
      dataFinal: dataFinalGlobal
    }))
  }, [
    dispatch,
    idProjetos,
    contextoValido,
    empresaPdcaExiste,
    empresaPdcaId,
    sistema.idiomaIdValue,
    dataInicialGlobal,
    dataFinalGlobal
  ])

  const contextoInconsistente = contextoCarregado && !empresaPdcaExiste
  const subtitle = `${total} ${total === 1 ? nls.summaryItemSingular : nls.summaryItemPlural}`

  const hojeInput = obterHojeInput()

  const dataInicialMax = dataFinal || hojeInput
  const dataFinalMin = dataInicial || ''

  const dataFinalMaxCalculada = dataInicial
    ? adicionarDiasInput(dataInicial, 59)
    : hojeInput

  const dataFinalMax = dataFinalMaxCalculada && dataFinalMaxCalculada < hojeInput
    ? dataFinalMaxCalculada
    : hojeInput

  const handlePesquisar = () => {
    if (!dataInicial || !dataFinal) {
      setErroFiltro(nls.filterErrorRequiredDates)
      return
    }

    if (dataFinal < dataInicial) {
      setErroFiltro(nls.filterErrorEndBeforeStart)
      return
    }

    if (diferencaDiasInput(dataInicial, dataFinal) > 59) {
      setErroFiltro(nls.filterErrorMaxRange)
      return
    }

    if (dataInicial > hojeInput || dataFinal > hojeInput) {
      setErroFiltro(nls.filterErrorFutureDate)
      return
    }

    setErroFiltro(null)

    dispatch(setPeriodoPdca({
      dataInicial,
      dataFinal
    }))
  }

  const handleSelecionarStatus = e => {
    setStatusSelecionado(e?.value || statusOptions[0] || {})
  }

  const handleDetalhes = planoId => {
    if (!planoId) {
      return
    }

    setPlanoIdSelecionado(planoId)
    setDetalheOpen(true)
  }

  const handleAbrirRejeitar = item => {
    if (!item?.id) {
      return
    }

    setPlanoIdRejeitar(item.id)
    setNomePlanoRejeitar(`${nls.planNumber} ${item.numeroPlano} - ${item.titulo}`)
    setRejeitarOpen(true)
  }

  const handleFecharRejeitar = () => {
    setRejeitarOpen(false)
    setPlanoIdRejeitar(null)
    setNomePlanoRejeitar('')
  }

  const handleSucessoRejeitar = () => {
    handleFecharRejeitar()

    dispatch(fetchDo({
      empresaId: empresaPdcaId,
      projetoId: idProjetos,
      idiomaId: sistema.idiomaIdValue,
      dataInicial: dataInicialGlobal,
      dataFinal: dataFinalGlobal
    }))
  }

  const handleAbrirAprovar = item => {
    if (!item?.id) {
      return
    }

    setPlanoIdAprovar(item.id)
    setNomePlanoAprovar(`${nls.planNumber} ${item.numeroPlano} - ${item.titulo}`)
    setAprovarOpen(true)
  }

  const handleFecharAprovar = () => {
    setAprovarOpen(false)
    setPlanoIdAprovar(null)
    setNomePlanoAprovar('')
  }

  const handleSucessoAprovar = () => {
    handleFecharAprovar()

    dispatch(fetchDo({
      empresaId: empresaPdcaId,
      projetoId: idProjetos,
      idiomaId: sistema.idiomaIdValue,
      dataInicial: dataInicialGlobal,
      dataFinal: dataFinalGlobal
    }))
  }

  const itensFiltrados = Array.isArray(itens)
    ? itens.filter(item => {
        if (statusSelecionado?.value === 'todos') {
          return true
        }

        return item.status === statusSelecionado?.value
      })
    : []

  return (
    <PdcaLayout>
      <div className='box-pdca-do'>
        <PdcaPageHeader
          title={nls.pageTitle}
          subtitle={subtitle}
        />

        <div className='box-pdca-do-search-box'>
          <div className='box-pdca-do-filters'>
            <div className='box-pdca-do-filter-field box-pdca-do-filter-field-date'>
              <label>{nls.filterStartDate}</label>
              <input
                type='date'
                value={dataInicial}
                max={dataInicialMax}
                onChange={e => {
                  const novoValor = e.target.value
                  setDataInicial(novoValor)

                  if (dataFinal) {
                    const dataFinalInvalida =
                      dataFinal < novoValor ||
                      diferencaDiasInput(novoValor, dataFinal) > 59

                    if (dataFinalInvalida) {
                      setDataFinal('')
                    }
                  }
                }}
              />
            </div>

            <div className='box-pdca-do-filter-field box-pdca-do-filter-field-date'>
              <label>{nls.filterEndDate}</label>
              <input
                type='date'
                value={dataFinal}
                min={dataFinalMin || undefined}
                max={dataFinalMax}
                onChange={e => setDataFinal(e.target.value)}
              />
            </div>

            <div className='box-pdca-do-filter-action'>
              <Button color='primary' onClick={handlePesquisar}>
                {nls.filterSearch}
              </Button>
            </div>

            <div className='box-pdca-do-status-filter'>
              <div className='box-pdca-do-status-select'>
                <Select
                  name='statusDo'
                  label={nls.filterStatus}
                  options={statusOptions}
                  selected={statusSelecionado || {}}
                  action={handleSelecionarStatus}
                  selectedItem={false}
                  optionLabel='label'
                  optionValue='value'
                />
              </div>
            </div>
          </div>
        </div>

        {erroFiltro ? (
          <div className='box-pdca-do-inline-error'>
            {erroFiltro}
          </div>
        ) : null}

        {contextoInconsistente ? (
          <div className='box-pdca-do-inline-error'>
            {nls.contextError}
          </div>
        ) : null}

        {!contextoInconsistente ? (
          <LoadingContent loading={loading}>
            {error ? (
              <div className='box-pdca-do-inline-error'>
                {nls.loadError}
              </div>
            ) : null}

            {!error ? (
              <>
                {itensFiltrados.length ? (
                  <div className='box-pdca-do-list'>
                                       
                    {itensFiltrados.map(item => {
                    const actions = [
                      { key: 'details', label: nls.actionDetails, primary: false }
                    ]

                    if (item.status !== 'atraso_critico' && podeGerirProjeto) {
                      actions.unshift(
                        {
                          key: 'approve',
                          label: nls.actionApprove,
                          primary: true,
                          onClick: () => handleAbrirAprovar(item)
                        },
                        {
                          key: 'reject',
                          label: nls.actionReject,
                          primary: false,
                          onClick: () => handleAbrirRejeitar(item)
                        }
                      )
                    }

                      return (
                        <PdcaPlanoCard
                          key={`do-${item.id}`}
                          item={item}
                          nls={nls}
                          titlePrefix={nls.planNumber}
                          typeLabel={obterTipoPlano(item.tipoPlano, nls)}
                          scopeText={obterEscopo(item, nls)}
                          badges={[
                            {
                              key: `status-${item.id}`,
                              variant: obterVariantStatus(item.status),
                              label: obterStatus(item.status, nls)
                            },
                            {
                              key: `tipo-${item.id}`,
                              variant: 'neutral',
                              label: obterTipoPlano(item.tipoPlano, nls)
                            }
                          ]}
                          metaItems={[
                            { label: nls.metaStatus, value: obterStatus(item.status, nls) },
                            { label: nls.metaDeadline, value: item.deadline },
                            { label: nls.metaActions, value: item.acoesResumo },
                            {
                              label: nls.metaDaysToCriticalDelay,
                              value: item.diasParaAtrasoCritico
                            }
                          ]}
                          actions={actions}
                          onDetalhes={handleDetalhes}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <PdcaEmptyState
                    title={
                      itens.length
                        ? nls.emptyStatusFilter
                        : nls.emptyState
                    }
                  />
                )}
              </>
            ) : null}
          </LoadingContent>
        ) : null}

        <Detalheplano
          open={detalheOpen}
          close={() => setDetalheOpen(false)}
          planoId={planoIdSelecionado}
        />

        <RejeitarPlano
          open={rejeitarOpen}
          close={handleFecharRejeitar}
          planoId={planoIdRejeitar}
          ciclo='do'
          nomePlano={nomePlanoRejeitar}
          onSuccess={handleSucessoRejeitar}
        />

        <AprovarAcao
          open={aprovarOpen}
          close={handleFecharAprovar}
          planoId={planoIdAprovar}
          tipoPlano=''
          nivel=''
          nomePlano={nomePlanoAprovar}
          onSuccess={handleSucessoAprovar}
        />
      </div>
    </PdcaLayout>
  )
}